// src/AdminPanel.js
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef
} from "react";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import "./AdminPanel.css";

/* ───────────────────────────────────────────────────────────── */
/*  Config                                                      */
const SHOW_SEARCH_COUNT = true;        // toggle project-count display
const BC_CHANNEL        = "projects";  // BroadcastChannel name
/* ───────────────────────────────────────────────────────────── */

// debounce helper
function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
}

// Title-case helper
const titleCase = str =>
  str
    .toLowerCase()
    .split(" ")
    .map(w => (w[0] ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");

/* =======================================================================
   <ProjectRow/> – one project, plus any sub-rows
   ===================================================================== */
function ProjectRow({
  p,
  editing,
  setEditing,
  startEdit,
  finishEdit,
  archiveProject,
  confirmDel,
  setConfirmDel,
  expanded,
  anyOpen,
  onToggle,
  updateField,
  setProjects,
  setNodeRef,
  attributes = {},
  listeners = {},
  transform,
  transition,
  isDragging,
  dragHeight
}) {
  const keyBase = `${p.project_id}_`;

  /* quick inline edit/save */
  const onBlurText = (field, value) => {
    updateField(p.project_id, field, value);
    setProjects(ps =>
      ps.map(x => (x.project_id === p.project_id ? { ...x, [field]: value } : x))
    );
  };

  const renderField = (field, cls) => {
    const key = keyBase + field;
    let val = p[field] ?? "";
    if (field === "project_type") val = titleCase(val);
    const empty = val === "";
    return (
      <span
        key={field}
        className={cls}
        onClick={() => startEdit(p.project_id, field, p[field] ?? "")}
      >
        {editing[key] ? (
          <input
            autoFocus
            value={editing[key].val}
            onChange={e =>
              setEditing(prev => ({
                ...prev,
                [key]: { ...prev[key], val: e.target.value }
              }))
            }
            onBlur={() => finishEdit(p.project_id, field, true)}
            onKeyDown={e =>
              e.key === "Escape" && finishEdit(p.project_id, field, false)
            }
          />
        ) : empty ? (
          <em className="placeholder">{field}</em>
        ) : (
          val
        )}
      </span>
    );
  };

  /* lock height while dragging */
  const rowRef = useRef(null);
  useEffect(() => {
    if (isDragging && rowRef.current) {
      rowRef.current.style.height = dragHeight + "px";
    }
  }, [isDragging, dragHeight]);

  const style = isDragging
    ? {
        position: "absolute",
        zIndex: 999,
        width: "100%",
        height: dragHeight,
        transform: CSS.Transform.toString(transform),
        backgroundColor: "black"
      }
    : { transform: CSS.Transform.toString(transform), transition };

  const classes = [
    "project-row",
    expanded && "expanded",
    isDragging && "dragging",
    anyOpen && !expanded && "dimmed"
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={classes}
      ref={el => {
        if (setNodeRef) setNodeRef(el);
        rowRef.current = el;
      }}
      style={style}
      {...attributes}
    >
      {/* Header */}
      <div
        className="project-header"
        onDoubleClick={e => {
          e.preventDefault();
          e.stopPropagation();
          onToggle(p.project_id);
        }}
      >
        {renderField("project_order", "project-order-field")}
        <span
          className="project-handle left-handle"
          {...listeners}
          onDoubleClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onToggle(p.project_id);
          }}
        >
          ☰
        </span>
        {[
          "project_name",
          "project_director",
          "project_company",
          "project_type",
          "project_scripts"
        ].map(f => renderField(f, "project-field"))}
        <label className="visible-toggle">
          Visible
          <input
            type="checkbox"
            checked={!!p.project_visible}
            onChange={e => onBlurText("project_visible", e.target.checked ? 1 : 0)}
          />
        </label>
        <span
          className="project-handle right-handle"
          {...listeners}
          onDoubleClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onToggle(p.project_id);
          }}
        >
          ☰
        </span>
        {confirmDel[p.project_id] ? (
          <>
            <button
              onClick={() => {
                archiveProject(p.project_id);
                setConfirmDel({});
              }}
            >
              Confirm
            </button>
            <button
              onClick={() =>
                setConfirmDel(cd => {
                  const c = { ...cd };
                  delete c[p.project_id];
                  return c;
                })
              }
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            onClick={() =>
              setConfirmDel(cd => ({ ...cd, [p.project_id]: true }))
            }
          >
            Delete
          </button>
        )}
      </div>

      {/* Sub-row */}
      {expanded && (
        <div className="project-subrow">
          <div className="sub-block">
            <label>Project Image:</label>
            <input
              defaultValue={p.project_coverImage}
              onBlur={e => onBlurText("project_coverImage", e.target.value)}
            />
          </div>
          <div className="sub-block">
            <label>Categories:</label>
            <input
              defaultValue={p.project_categories}
              onBlur={e => onBlurText("project_categories", e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Summary / Treatment */}
      {expanded && (
        <div className="expanded-content">
          <div className="fields-side-by-side">
            <div className="field-block">
              <label>Summary Text:</label>
              <textarea
                defaultValue={p.project_fullText}
                onBlur={e => onBlurText("project_fullText", e.target.value)}
              />
            </div>
            <div className="field-block">
              <label>Treatment Text:</label>
              <textarea
                defaultValue={p.project_treatmentText}
                onBlur={e =>
                  onBlurText("project_treatmentText", e.target.value)
                }
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Sortable wrapper for <ProjectRow/> */
function SortableProjectRow({ dragHeight, anyOpen, expanded, ...props }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: props.p.project_id });

  const placeholderStyle = isDragging
    ? { height: `${dragHeight}px`, visibility: "hidden" }
    : {};

  return (
    <div style={placeholderStyle}>
      <ProjectRow
        {...props}
        anyOpen={anyOpen}
        expanded={expanded}
        setNodeRef={setNodeRef}
        attributes={attributes}
        listeners={listeners}
        transform={transform}
        transition={transition}
        isDragging={isDragging}
        dragHeight={dragHeight}
      />
    </div>
  );
}

/* AdminPanel – main component */
export default function AdminPanel() {
  /* state */
  const [loggedIn, setLoggedIn]   = useState(false);
  const [username,  setUsername]  = useState("");
  const [password,  setPassword]  = useState("");
  const [projects,  setProjects]  = useState([]);
  const [editing,   setEditing]   = useState({});
  const [confirmDel, setConfirmDel] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [activeId,  setActiveId]  = useState(null);
  const [dragHeight,setDragHeight]= useState(0);
  const [searchTerm,setSearchTerm]= useState("");
  const [showHidden,setShowHidden]= useState(false);

  /* refs */
  const listRef = useRef(null);
  const bcRef   = useRef(null);

  /* API base */
  const apiBase =
    window.location.hostname === "localhost"
      ? "http://localhost:8000/gateway.php"
      : window.location.origin + "/backend/public/gateway.php";

  /* BroadcastChannel + debounced notifier */
  useEffect(() => {
    bcRef.current = new BroadcastChannel(BC_CHANNEL);
    return () => bcRef.current.close();
  }, []);
  const postUpdate = useMemo(() => debounce(() => {
    bcRef.current?.postMessage("updated");
  }, 1000), []);

  /* Auth check */
  useEffect(() => {
    fetch(`${apiBase}?action=checkAuth`, { credentials: "include" })
      .then(r => r.json())
      .then(d => d.isAdmin && setLoggedIn(true))
      .catch(console.error);
  }, [apiBase]);

  /* Load projects */
  useEffect(() => {
    if (!loggedIn) return;
    fetch(`${apiBase}?action=getProjectsAdmin`, { credentials: "include" })
      .then(r => r.json())
      .then(setProjects)
      .catch(console.error);
  }, [loggedIn, apiBase]);

  /* === CRUD helpers === */
  const updateField = useCallback(
    (id, field, value) => {
      fetch(`${apiBase}?action=updateProjectField`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, field, value })
      })
      .then(r => r.json())
      .then(res => {
        if (!res.success) alert("Update failed");
        else postUpdate();
      });
    },
    [apiBase, postUpdate]
  );
  const debouncedUpdateField = useMemo(() => debounce(updateField, 500), [updateField]);

  const addProject = () => {
    fetch(`${apiBase}?action=addProject`, {
      method: "POST",
      credentials: "include"
    })
      .then(r => r.json())
      .then(res => {
        if (res.success) {
          setProjects(p => [...p, res.project]);
          setExpandedRows({});
          setConfirmDel({});
          postUpdate();
          setTimeout(() => {
            listRef.current?.lastElementChild?.scrollIntoView({
              behavior: "smooth",
              block: "end"
            });
          }, 50);
        }
      });
  };

  const archiveProject = id => {
    fetch(`${apiBase}?action=archiveProject`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id })
    })
      .then(r => r.json())
      .then(res => {
        if (res.success) {
          setProjects(p => p.filter(x => x.project_id !== id));
          setExpandedRows({});
          setConfirmDel({});
          postUpdate();
        }
      });
  };

  /* Login / Logout */
  const handleLogin = e => {
    e.preventDefault();
    fetch(`${apiBase}?action=login`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password })
    })
      .then(r => r.json())
      .then(d => (d.success ? setLoggedIn(true) : alert("Login failed")));
  };
  const handleLogout = () => {
    fetch(`${apiBase}?action=logout`, {
      method: "POST",
      credentials: "include"
    })
      .then(() => {
        setLoggedIn(false);
        postUpdate();
      })
      .catch(console.error);
  };

  /* Inline edit helpers */
  const startEdit = (id, field, val) =>
    setEditing(e => ({ ...e, [`${id}_${field}`]: { val, orig: val } }));
  const finishEdit = (id, field, save) => {
    const key = `${id}_${field}`;
    const { val, orig } = editing[key] || {};
    setEditing(e => {
      const r = { ...e };
      delete r[key];
      return r;
    });
    if (save && val !== orig) {
      setProjects(ps =>
        ps.map(p => (p.project_id === id ? { ...p, [field]: val } : p))
      );
      debouncedUpdateField(id, field, val);
    }
  };

  /* Expand toggle */
  const toggleExpand = id =>
    setExpandedRows(prev => (prev[id] ? {} : { [id]: true }));

  /* Drag-n-drop */
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { delay: 150, tolerance: 5 }
    })
  );
  const handleDragStart = ({ active }) => {
    setActiveId(active.id);
    const node = document.querySelector(`[data-key='row-${active.id}']`);
    if (node) setDragHeight(node.offsetHeight);
  };
  const handleDragEnd = ({ active, over }) => {
    if (over && active.id !== over.id) {
      const oldI = projects.findIndex(x => x.project_id === active.id);
      const newI = projects.findIndex(x => x.project_id === over.id);
      const reordered = arrayMove(projects, oldI, newI);
      setProjects(reordered);

      // bulkUpdateOrder in one API call
      fetch(`${apiBase}?action=bulkUpdateOrder`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order: reordered.map(p => p.project_id) })
      })
        .then(r => r.json())
        .then(res => {
          if (!res.success) alert("Reorder failed");
          else postUpdate();
        });
    }
    setActiveId(null);
  };

  /* Filtering */
  const displayedProjects = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return projects.filter(p => {
      if (showHidden && Number(p.project_visible) !== 0) return false;
      if (!term) return true;
      return ["project_name","project_description","project_director","project_company"].some(
        fld => (p[fld]||"").toLowerCase().includes(term)
      );
    });
  }, [projects, searchTerm, showHidden]);
  const anyOpen = Object.keys(expandedRows).length > 0;

  /* Render */
  if (!loggedIn) {
    return (
      <div className="admin-login">
        <form className="login-form" onSubmit={handleLogin}>
          <h2>Admin Login</h2>
          <input
            value={username}
            onChange={e => setUsername(e.target.value)}
            placeholder="Username"
            autoFocus
          />
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Password"
          />
          <button type="submit">Log In</button>
        </form>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      {/* Top search bar */}
      {SHOW_SEARCH_COUNT && (
        <div className="top-bar">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search projects…"
              value={searchTerm}
              onChange={e => {
                setSearchTerm(e.target.value);
                setExpandedRows({});
              }}
            />
            <span className="result-count">{displayedProjects.length}</span>
          </div>
          <label>
            <input
              type="checkbox"
              checked={showHidden}
              onChange={e => {
                setShowHidden(e.target.checked);
                setExpandedRows({});
              }}
            />
            Show Hidden
          </label>
        </div>
      )}

      {/* Draggable list */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveId(null)}
      >
        <SortableContext
          items={displayedProjects.map(p => p.project_id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="projects-list" ref={listRef}>
            {displayedProjects.map(p => (
              <SortableProjectRow
                key={p.project_id}
                p={p}
                editing={editing}
                setEditing={setEditing}
                startEdit={startEdit}
                finishEdit={finishEdit}
                archiveProject={archiveProject}
                confirmDel={confirmDel}
                setConfirmDel={setConfirmDel}
                expanded={!!expandedRows[p.project_id]}
                anyOpen={anyOpen}
                onToggle={toggleExpand}
                updateField={updateField}
                setProjects={setProjects}
                dragHeight={dragHeight}
                setNodeRef={el => {
                  if (el) el.dataset.key = `row-${p.project_id}`;
                }}
              />
            ))}
          </div>
        </SortableContext>

        <DragOverlay>
          {activeId && (
            <ProjectRow
              p={projects.find(x => x.project_id === activeId)}
              editing={editing}
              setEditing={() => {}}
              startEdit={() => {}}
              finishEdit={() => {}}
              archiveProject={() => {}}
              confirmDel={{}}
              setConfirmDel={() => {}}
              expanded={!!expandedRows[activeId]}
              anyOpen={false}
              onToggle={() => {}}
              updateField={() => {}}
              setProjects={() => {}}
              isDragging
              transform={{ x: 0, y: 0 }}
              transition={null}
              dragHeight={dragHeight}
            />
          )}
        </DragOverlay>
      </DndContext>

      {/* Bottom buttons */}
      <div className="add-project-container">
        <button className="add-project-button" onClick={addProject}>
          Add Project
        </button>
        <button
          className="site-view-button"
          onClick={() => (window.location.href = "/")}
        >
          Site View
        </button>
        <button className="logout-button" onClick={handleLogout}>
          Logout
        </button>
      </div>
    </div>
  );
}